import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { FileText, Upload, CheckCircle, XCircle, AlertTriangle, FileCheck, Download, Sparkles, Mail, X, Brain, Info, LogIn, LogOut, User, Coffee, Heart, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import CdarValidator from "./CdarValidator";
import CiiValidator from "./CiiValidator";
import { useAuth } from "../contexts/AuthContext";
import AuthModal from "../components/AuthModal";
import { AiExplainCard, AiLockedCard } from "../components/AiExplain";
import { PdfDownloadCard } from "../components/PdfPreview";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const ValidatorPage = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState(null);
  const [showBanner, setShowBanner] = useState(true);
  const [fileSizeError, setFileSizeError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["ubl", "cii", "cdar"].includes(t) ? t : "ubl";
  });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [rateLimit, setRateLimit] = useState(null);
  const fileInputRef = useRef(null);
  const isSupporter = !!(user && (user.is_premium || user.role === "admin"));

  const fetchRateLimit = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/rate-limit`, { withCredentials: true });
      setRateLimit(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchRateLimit();
  }, [fetchRateLimit, user]);

  // Set anonymous tracking cookie if not present
  useEffect(() => {
    if (!document.cookie.includes("_vid=")) {
      const vid = Math.random().toString(36).substring(2) + Date.now().toString(36);
      document.cookie = `_vid=${vid}; path=/; max-age=31536000; samesite=lax`;
    }
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.xml')) {
      if (droppedFile.size > MAX_FILE_SIZE) {
        setFileSizeError(`Le fichier (${(droppedFile.size / (1024*1024)).toFixed(2)} Mo) dépasse la limite de 5 Mo.`);
        setFile(null);
        return;
      }
      setFileSizeError(null);
      setFile(droppedFile);
      setResult(null);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > MAX_FILE_SIZE) {
        setFileSizeError(`Le fichier (${(selectedFile.size / (1024*1024)).toFixed(2)} Mo) dépasse la limite de 5 Mo.`);
        setFile(null);
        return;
      }
      setFileSizeError(null);
      setFile(selectedFile);
      setResult(null);
    }
  };

  const resetValidation = () => {
    setFile(null);
    setResult(null);
    setFileSizeError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleValidate = async () => {
    if (!file) return;

    setValidating(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/validate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      setResult(response.data);
      fetchRateLimit();
    } catch (error) {
      console.error('Validation error:', error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 413) {
        setFileSizeError(detail || "Le fichier dépasse la limite de 5 Mo.");
      } else if (status === 429) {
        setResult({
          valid: false,
          errors: [{ message: detail || "Limite de validations atteinte", level: "error" }],
          warnings: [],
          summary: "Limite de validations atteinte"
        });
        fetchRateLimit();
      } else {
        setResult({
          valid: false,
          errors: [{ message: detail || "Erreur lors de la validation", level: "error" }],
          warnings: [],
          summary: "Échec de la validation"
        });
      }
    } finally {
      setValidating(false);
    }
  };

  const downloadReport = () => {
    if (!result) return;
    
    const report = {
      date_validation: result.validation_date,
      fichier: file.name,
      taille: result.file_size,
      type_document: result.document_type,
      statut: result.valid ? "VALIDE" : "INVALIDE",
      resume: result.summary,
      etapes_validation: result.validation_stages || {},
      erreurs_xsd: result.xsd_errors || [],
      erreurs_schematron: result.schematron_errors || [],
      controle_ia: result.ai_validation || null,
      erreurs: [...(result.xsd_errors || []), ...(result.schematron_errors || [])],
      avertissements: result.warnings || []
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapport-validation-${new Date().getTime()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-2.5 rounded-xl shadow-lg">
                <FileCheck className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Valideur Réforme Électronique France</h1>
                <p className="text-sm text-slate-600">Conforme AFNOR XP Z12-012 du 30/06/2026 • Version 2.1</p>
              </div>
            </div>
            {/* Auth + Rate Limit */}
            <div className="flex items-center gap-3">
              {rateLimit && (
                <div className="hidden sm:flex items-center gap-2 text-sm" data-testid="rate-limit-display">
                  <span className={`font-semibold mr-1 ${rateLimit.remaining <= 1 ? 'text-red-600' : 'text-slate-600'}`}>
                    {rateLimit.remaining}/{rateLimit.limit}
                  </span>
                  <span className="text-slate-400">{rateLimit.period === "total" ? "valid. gratuites" : `valid./${rateLimit.period}`}</span>
                </div>
              )}
              {!authLoading && user ? (
                <div className="flex items-center gap-2">
                  <span className="hidden sm:inline text-sm text-slate-600" data-testid="user-name">
                    {user.name || user.email?.split("@")[0]}
                  </span>
                  {user.is_premium && (
                    <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">Supporter</Badge>
                  )}
                  {user.role === "admin" && (
                    <a href="/admin" data-testid="admin-link">
                      <Button variant="ghost" size="sm" className="text-purple-600 hover:text-purple-700 hover:bg-purple-50">
                        <Shield className="h-4 w-4" />
                      </Button>
                    </a>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={logout}
                    data-testid="logout-button"
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              ) : !authLoading ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAuthModal(true)}
                  data-testid="login-button"
                  className="gap-2 border-blue-200 text-blue-600 hover:bg-blue-50"
                >
                  <LogIn className="h-4 w-4" />
                  Connexion
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      {/* Auth Modal */}
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />

      {/* Announcement Banner */}
      {showBanner && (
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 text-white">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold" data-testid="announcement-banner-title">
                    Version 2.1 — Conforme à la norme AFNOR XP Z12-012 du 30/06/2026 (Schematron V1.4.0, norme 2026)
                  </p>
                  <p className="text-sm text-blue-100">
                    Un bug ? Une idée d'amélioration ? N'hésitez pas à me contacter, vos retours sont précieux !{' '}
                    <a
                      href="mailto:Q.levasseur@epmistes.net"
                      className="inline-flex items-center gap-1 underline hover:text-white font-semibold"
                    >
                      <Mail className="h-3.5 w-3.5" />
                      Q.levasseur@epmistes.net
                    </a>
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setShowBanner(false)}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Fermer"
                data-testid="announcement-banner-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-1 mb-8 bg-slate-100 p-1 rounded-xl w-fit" data-testid="tab-navigation">
          <button
            data-testid="tab-ubl"
            onClick={() => setActiveTab("ubl")}
            className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
              activeTab === "ubl"
                ? "bg-white text-blue-700 shadow-sm"
                : "text-slate-500 hover:text-slate-700 hover:bg-white/50"
            }`}
          >
            UBL / Facture
          </button>
          <button
            data-testid="tab-cii"
            onClick={() => setActiveTab("cii")}
            className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
              activeTab === "cii"
                ? "bg-white text-violet-700 shadow-sm"
                : "text-slate-500 hover:text-slate-700 hover:bg-white/50"
            }`}
          >
            Factur-X / CII
          </button>
          <button
            data-testid="tab-cdar"
            onClick={() => setActiveTab("cdar")}
            className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
              activeTab === "cdar"
                ? "bg-white text-teal-700 shadow-sm"
                : "text-slate-500 hover:text-slate-700 hover:bg-white/50"
            }`}
          >
            CDAR
          </button>
        </div>

        {/* Rate Limit Warning */}
        {rateLimit && rateLimit.remaining === 0 && (
          <Alert className="mb-6 bg-amber-50 border-amber-200" data-testid="rate-limit-warning">
            <AlertDescription className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm">
                  {!user && <>Vous avez utilisé vos {rateLimit.limit} validations gratuites. <button onClick={() => setShowAuthModal(true)} className="font-semibold underline hover:text-amber-900">Créez un compte gratuit</button> pour bénéficier de 5 validations par jour.</>}
                  {user && <>Vous avez atteint la limite de {rateLimit.limit} validations par {rateLimit.period}.</>}
                  {user && !user.is_premium && <> Revenez demain ou <a href="https://paypal.me/LevasseurQuentin/2EUR" target="_blank" rel="noopener noreferrer" className="font-semibold underline hover:text-amber-900">soutenez le projet</a> pour passer à 1000/mois.</>}
                </span>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Anonymous incentive banner */}
        {!user && rateLimit && rateLimit.remaining > 0 && rateLimit.period === "total" && (
          <Alert className="mb-6 bg-blue-50 border-blue-200" data-testid="anon-incentive-banner">
            <AlertDescription className="flex items-center justify-between gap-4 flex-wrap">
              <span className="text-sm text-blue-800">
                Mode anonyme : il vous reste <strong>{rateLimit.remaining} validation{rateLimit.remaining > 1 ? "s" : ""} gratuite{rateLimit.remaining > 1 ? "s" : ""}</strong> (au total).{" "}
                <button onClick={() => setShowAuthModal(true)} className="font-semibold underline hover:text-blue-900" data-testid="anon-incentive-signup">Créez un compte gratuit</button>
                {" "}et profitez de 5 validations par jour, tous les jours.
              </span>
            </AlertDescription>
          </Alert>
        )}

        {/* CDAR Tab */}
        {activeTab === "cdar" && <CdarValidator />}

        {/* CII Tab */}
        {activeTab === "cii" && <CiiValidator />}

        {/* UBL Tab */}
        {activeTab === "ubl" && (
        <div>
        {/* Upload Section */}
        <Card className="mb-8 shadow-xl border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-800">
              <Upload className="h-5 w-5 text-blue-600" />
              Importer un fichier UBL
            </CardTitle>
            <CardDescription className="text-slate-600">
              Téléchargez votre fichier XML UBL pour valider sa conformité (Invoice, CreditNote, Order) — Max 5 Mo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              data-testid="upload-dropzone"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all duration-300 ${
                isDragging
                  ? 'border-blue-500 bg-blue-50/50 scale-[1.02]'
                  : 'border-slate-300 bg-slate-50/50 hover:border-blue-400 hover:bg-blue-50/30'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xml"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              <FileText className={`h-16 w-16 mx-auto mb-4 transition-colors ${
                isDragging ? 'text-blue-600' : 'text-slate-400'
              }`} />
              {file ? (
                <div>
                  <p className="text-lg font-semibold text-slate-800 mb-1">{file.name}</p>
                  <p className="text-sm text-slate-500">{formatFileSize(file.size)}</p>
                </div>
              ) : (
                <div>
                  <p className="text-lg font-medium text-slate-700 mb-1">
                    Glissez-déposez votre fichier XML ici
                  </p>
                  <p className="text-sm text-slate-500">ou cliquez pour parcourir</p>
                </div>
              )}
            </div>

            {fileSizeError && (
              <Alert variant="destructive" className="mt-4 bg-red-50 border-red-200" data-testid="file-size-error">
                <AlertDescription className="flex items-center gap-2 text-red-700">
                  <XCircle className="h-4 w-4 flex-shrink-0" />
                  {fileSizeError}
                </AlertDescription>
              </Alert>
            )}

            {file && (
              <div className="flex gap-3 mt-6">
                <Button
                  data-testid="validate-button"
                  onClick={handleValidate}
                  disabled={validating}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-6 rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl"
                >
                  {validating ? (
                    <span className="flex items-center gap-2">
                      <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Validation en cours...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <FileCheck className="h-5 w-5" />
                      {result ? 'Revalider le document' : 'Valider le document'}
                    </span>
                  )}
                </Button>
                <Button
                  data-testid="clear-button"
                  onClick={resetValidation}
                  variant="outline"
                  className="px-6 py-6 rounded-xl border-slate-300 hover:bg-slate-100"
                >
                  Nouveau fichier
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results Section */}
        {result && (
          <div className="space-y-6">
            {/* Summary Card */}
            <Card className="shadow-xl border-slate-200">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    {result.valid ? (
                      <div className="bg-green-100 p-3 rounded-xl">
                        <CheckCircle className="h-8 w-8 text-green-600" />
                      </div>
                    ) : (
                      <div className="bg-red-100 p-3 rounded-xl">
                        <XCircle className="h-8 w-8 text-red-600" />
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-2xl mb-2 text-slate-800">
                        {result.valid ? 'Document valide' : 'Document invalide'}
                      </CardTitle>
                      <CardDescription className="text-base text-slate-600">
                        {result.summary}
                      </CardDescription>
                    </div>
                  </div>
                  <Button
                    data-testid="download-report-button"
                    onClick={downloadReport}
                    variant="outline"
                    className="gap-2 rounded-lg border-slate-300 hover:bg-slate-100"
                  >
                    <Download className="h-4 w-4" />
                    Télécharger le rapport
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Validation Stages */}
                  {result.validation_stages && (
                    <div className="bg-slate-50 p-4 rounded-xl">
                      <p className="text-sm font-semibold text-slate-700 mb-3">Étapes de validation :</p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-slate-600">Structure XML:</span>
                          <span className={result.validation_stages.xml_structure?.includes('✓') ? 'text-green-600' : 'text-red-600'}>
                            {result.validation_stages.xml_structure}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-slate-600">Schéma XSD UBL 2.1:</span>
                          <span className={result.validation_stages.xsd_schema?.includes('✓') ? 'text-green-600' : 'text-red-600'}>
                            {result.validation_stages.xsd_schema}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-slate-600">Règles métier FR (BR-FR):</span>
                          <span className={result.validation_stages.schematron_br_fr?.includes('✓') ? 'text-green-600' : result.validation_stages.schematron_br_fr?.includes('⊘') ? 'text-slate-400' : 'text-red-600'}>
                            {result.validation_stages.schematron_br_fr}
                          </span>
                        </div>
                        {result.validation_stages.ai_coherence && (
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-medium text-slate-600">Contrôle IA:</span>
                            <span className={result.validation_stages.ai_coherence?.includes('✓') ? 'text-green-600' : result.validation_stages.ai_coherence?.includes('⊘') ? 'text-slate-400' : 'text-amber-600'}>
                              {result.validation_stages.ai_coherence}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-slate-50 p-4 rounded-xl">
                      <p className="text-xs text-slate-500 mb-1">Type de document</p>
                      <p className="font-semibold text-slate-800">{result.document_type || 'N/A'}</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl">
                      <p className="text-xs text-slate-500 mb-1">Taille du fichier</p>
                      <p className="font-semibold text-slate-800">{formatFileSize(result.file_size)}</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl">
                      <p className="text-xs text-slate-500 mb-1">Erreurs XSD</p>
                      <p className="font-semibold text-red-600">{result.xsd_errors?.length || 0}</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl">
                      <p className="text-xs text-slate-500 mb-1">Erreurs BR-FR</p>
                      <p className="font-semibold text-amber-600">{result.schematron_errors?.length || 0}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI error analysis (failed validation) — mise en avant */}
            {!result.valid && result.validation_id && (
              <AiExplainCard validationId={result.validation_id} />
            )}

            {/* XSD Errors */}
            {result.xsd_errors && result.xsd_errors.length > 0 && (
              <Card className="shadow-xl border-red-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <XCircle className="h-5 w-5" />
                    Erreurs XSD Schema ({result.xsd_errors.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px] pr-4">
                    <div className="space-y-3">
                      {result.xsd_errors.map((error, index) => (
                        <Alert key={index} variant="destructive" className="bg-red-50 border-red-200">
                          <AlertDescription className="text-slate-800">
                            <div className="flex items-start gap-3">
                              <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="font-medium mb-1">{error.message}</p>
                                {(error.line || error.element) && (
                                  <div className="flex gap-3 text-sm text-slate-600 mt-2">
                                    {error.line && (
                                      <Badge variant="outline" className="bg-white border-red-300">
                                        Ligne {error.line}
                                      </Badge>
                                    )}
                                    {error.column && (
                                      <Badge variant="outline" className="bg-white border-red-300">
                                        Colonne {error.column}
                                      </Badge>
                                    )}
                                    {error.element && (
                                      <Badge variant="outline" className="bg-white border-red-300">
                                        {error.element}
                                      </Badge>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </AlertDescription>
                        </Alert>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}

            {/* Schematron BR-FR Errors */}
            {result.schematron_errors && result.schematron_errors.length > 0 && (
              <Card className="shadow-xl border-amber-200" data-testid="schematron-errors-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="h-5 w-5" />
                    Erreurs Règles Métier FR ({result.schematron_errors.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px] pr-4">
                    <div className="space-y-3">
                      {result.schematron_errors.map((error, index) => (
                        <Alert key={index} className="bg-amber-50 border-amber-200">
                          <AlertDescription className="text-slate-800">
                            <div className="flex items-start gap-3">
                              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="font-medium mb-1">{error.message}</p>
                                {(error.test || error.location || error.rule_id) && (
                                  <div className="flex flex-wrap gap-2 text-sm text-slate-600 mt-2">
                                    {error.rule_id && (
                                      <Badge variant="outline" className="bg-white border-amber-300">
                                        {error.rule_id}
                                      </Badge>
                                    )}
                                    {error.location && (
                                      <Badge variant="outline" className="bg-white border-amber-300">
                                        {error.location}
                                      </Badge>
                                    )}
                                    {error.test && (
                                      <Badge variant="outline" className="bg-white border-amber-300 text-xs">
                                        Test: {error.test}
                                      </Badge>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </AlertDescription>
                        </Alert>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}

            {/* AI Coherence Check Results */}
            {result.ai_validation && result.ai_validation.enabled && (
              <Card className="shadow-xl border-indigo-200" data-testid="ai-validation-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-indigo-700">
                    <Brain className="h-5 w-5" />
                    Contrôle de Cohérence IA
                    <Badge variant="outline" className="ml-2 border-indigo-300 text-indigo-600 text-xs font-normal">
                      GPT-4o
                    </Badge>
                  </CardTitle>
                  <CardDescription className="text-slate-500">
                    Analyse intelligente des montants, calculs TVA et cohérence logique du document
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!result.ai_validation.analyzed && result.ai_validation.error_message && (
                    <Alert className="bg-slate-50 border-slate-200">
                      <AlertDescription className="flex items-center gap-2 text-slate-600">
                        <Info className="h-4 w-4 flex-shrink-0" />
                        {result.ai_validation.error_message}
                      </AlertDescription>
                    </Alert>
                  )}

                  {!result.ai_validation.analyzed && !result.ai_validation.error_message && (
                    <Alert className="bg-slate-50 border-slate-200">
                      <AlertDescription className="flex items-center gap-2 text-slate-500">
                        <Info className="h-4 w-4 flex-shrink-0" />
                        Contrôle IA non exécuté (le document doit d'abord passer les validations XSD et Schematron).
                      </AlertDescription>
                    </Alert>
                  )}

                  {result.ai_validation.analyzed && (
                    <div className="space-y-4">
                      {/* Confidence Score */}
                      {result.ai_validation.confidence_score != null && (
                        <div className="flex items-center gap-4 bg-slate-50 border border-slate-200 p-4 rounded-xl" data-testid="ai-confidence-score">
                          <div className="relative h-16 w-16 flex-shrink-0">
                            <svg className="h-16 w-16 -rotate-90" viewBox="0 0 36 36">
                              <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                              <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                fill="none"
                                stroke={result.ai_validation.confidence_score >= 80 ? '#22c55e' : result.ai_validation.confidence_score >= 50 ? '#f59e0b' : '#ef4444'}
                                strokeWidth="3"
                                strokeDasharray={`${result.ai_validation.confidence_score}, 100`}
                              />
                            </svg>
                            <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-slate-800">
                              {result.ai_validation.confidence_score}%
                            </span>
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800">Score de confiance</p>
                            <p className="text-xs text-slate-500">
                              {result.ai_validation.confidence_score >= 80 ? 'Document fiable — aucune anomalie majeure' :
                               result.ai_validation.confidence_score >= 50 ? 'Attention — vérifications recommandées' :
                               'Document à risque — anomalies détectées'}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* SIRET Check Results */}
                      {result.ai_validation.siret_check && result.ai_validation.siret_check.checks && result.ai_validation.siret_check.checks.length > 0 && (
                        <div className="bg-slate-50 border border-slate-200 p-4 rounded-xl" data-testid="ai-siret-check">
                          <p className="text-sm font-semibold text-slate-700 mb-2">Vérification SIRET/SIREN (annuaire-entreprises.data.gouv.fr)</p>
                          <div className="space-y-2">
                            {result.ai_validation.siret_check.checks.map((check, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm">
                                {check.found ? (
                                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                                ) : check.checked ? (
                                  <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                                ) : (
                                  <Info className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                )}
                                <span className="text-slate-600">
                                  <span className="font-medium">{check.party}</span> : {check.found ? `${check.nom}` : check.message || check.reason || 'Non vérifié'}
                                  {check.found && check.etat === 'C' && <span className="text-red-600 font-medium"> (cessée)</span>}
                                  {check.found && check.etat !== 'C' && <span className="text-green-600"> — actif</span>}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* TVA Check */}
                      {result.ai_validation.tva_check && result.ai_validation.tva_check.checked && (
                        <div className="bg-slate-50 border border-slate-200 p-4 rounded-xl" data-testid="ai-tva-check">
                          <p className="text-sm font-semibold text-slate-700 mb-1">Contrôle des taux de TVA</p>
                          <p className="text-xs text-slate-500">
                            Taux utilisés : {result.ai_validation.tva_check.rates_found?.map(r => `${r}%`).join(', ') || 'aucun'}
                            {result.ai_validation.tva_check.non_standard_count > 0
                              ? ` — ${result.ai_validation.tva_check.non_standard_count} taux non standard`
                              : ' — tous conformes aux taux légaux français'}
                          </p>
                        </div>
                      )}

                      {/* AI Summary */}
                      {result.ai_validation.summary && (
                        <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-xl">
                          <p className="text-sm text-indigo-800">{result.ai_validation.summary}</p>
                        </div>
                      )}

                      {/* AI Issues */}
                      {result.ai_validation.issues && result.ai_validation.issues.length > 0 ? (
                        <div className="space-y-3">
                          {result.ai_validation.issues.map((issue, index) => (
                            <Alert
                              key={index}
                              className={issue.severity === 'error' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}
                              data-testid={`ai-issue-${index}`}
                            >
                              <AlertDescription className="text-slate-800">
                                <div className="flex items-start gap-3">
                                  {issue.severity === 'error' ? (
                                    <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                                  ) : (
                                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                  )}
                                  <div className="flex-1">
                                    <p className="font-medium mb-1">{issue.message}</p>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      <Badge variant="outline" className={`text-xs ${issue.severity === 'error' ? 'border-red-300 bg-white' : 'border-amber-300 bg-white'}`}>
                                        {issue.issue_type}
                                      </Badge>
                                      {issue.expected_value && (
                                        <Badge variant="outline" className="text-xs border-green-300 bg-green-50">
                                          Attendu : {issue.expected_value}
                                        </Badge>
                                      )}
                                      {issue.found_value && (
                                        <Badge variant="outline" className="text-xs border-red-300 bg-red-50">
                                          Trouvé : {issue.found_value}
                                        </Badge>
                                      )}
                                      {issue.location && (
                                        <Badge variant="outline" className="text-xs border-slate-300 bg-white">
                                          {issue.location}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </AlertDescription>
                            </Alert>
                          ))}
                        </div>
                      ) : (
                        <Alert className="bg-green-50 border-green-200">
                          <AlertDescription className="flex items-center gap-2 text-green-700">
                            <CheckCircle className="h-5 w-5 flex-shrink-0" />
                            Aucune anomalie de cohérence détectée par l'IA.
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* AI locked notice (non-supporters, valid document) */}
            {result.valid && !isSupporter && result.validation_stages && (
              <AiLockedCard />
            )}

            {/* Readable PDF preview (supporters) */}
            {result.validation_id && (
              <PdfDownloadCard validationId={result.validation_id} />
            )}
          </div>
        )}
        </div>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-16 border-t border-slate-200 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6 py-12">
          {/* Main Footer Content */}
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* About Section */}
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Valideur UBL, CII & CDAR</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-2">
                Outil gratuit de validation de factures électroniques UBL, Factur-X/CII et de flux CDAR (cycle de vie) conforme à la norme AFNOR FE – XP Z12-012 pour la réforme française de facturation électronique 2026.
              </p>
              <p className="text-xs text-blue-600 font-medium">
                Schematron BR-FR & BR-FR-CDV V1.4.0 — Norme AFNOR XP Z12-012 du 30/06/2026
              </p>
              <nav className="mt-4 flex flex-col gap-1.5 text-sm">
                <a href="/ubl" className="text-blue-600 hover:underline" data-testid="footer-link-ubl">→ Guide UBL 2.1 — validation XSD & BR-FR</a>
                <a href="/factur-x" className="text-blue-600 hover:underline" data-testid="footer-link-facturx">→ Guide Factur-X — norme 2026</a>
                <a href="/cii" className="text-blue-600 hover:underline" data-testid="footer-link-cii">→ Guide CII (UN/CEFACT) — validation EN16931</a>
                <a href="/cdar" className="text-blue-600 hover:underline" data-testid="footer-link-cdar">→ Guide CDAR — statuts de cycle de vie</a>
              </nav>
            </div>

            {/* Standards Section */}
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Normes & Standards</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2">
                  <span className="text-blue-600">•</span> AFNOR FE – XP Z12-012
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-blue-600">•</span> Schematron BR-FR FNFE V1.4.0 (norme 2026)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-blue-600">•</span> Schematron BR-FR-CDV V1.4.0
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-blue-600">•</span> UBL 2.1 (OASIS) / EN16931
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-blue-600">•</span> CDAR (CrossDomainAcknowledgementAndResponse)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-blue-600">•</span> Contrôle de cohérence IA (GPT-4o)
                </li>
              </ul>
            </div>

            {/* Contact Section */}
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Contact</h3>
              <div className="space-y-3">
                <a 
                  href="mailto:Q.levasseur@epmistes.net"
                  className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Q.levasseur@epmistes.net
                </a>
                <p className="text-xs text-slate-500">
                  Bug, suggestion d'amélioration ou question technique
                </p>
              </div>
            </div>
          </div>

          {/* Buy me a coffee */}
          <div className="border-t border-slate-200 pt-8 mb-6">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6 text-center" data-testid="buy-me-a-coffee">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Coffee className="h-5 w-5 text-amber-700" />
                <h4 className="text-base font-semibold text-amber-900">Soutenez ce projet</h4>
              </div>
              <p className="text-sm text-amber-800 mb-4 max-w-lg mx-auto">
                Ce valideur est gratuit et le restera. Si vous l'utilisez régulièrement et qu'il vous fait gagner du temps, 
                offrez-moi un café pour m'aider à maintenir et améliorer l'outil.
              </p>
              <a
                href="https://paypal.me/LevasseurQuentin/2EUR"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="buy-coffee-link"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-2.5 px-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
              >
                <Heart className="h-4 w-4" />
                Offrir un café — 2€
              </a>
              <p className="text-xs text-amber-700 mt-3 bg-amber-100/50 rounded-lg py-2 px-4 inline-block">
                Lors du paiement, sélectionnez <strong>"Envoyer à un proche"</strong> (option Personnel) pour éviter les frais.
              </p>
              <p className="text-xs text-amber-600 mt-2 flex items-center justify-center gap-1">
                <Sparkles className="h-3 w-3" />
                Les supporters bénéficient de 1000 validations par mois, du contrôle IA, de l'explication IA des erreurs de non-conformité, de la vérification annuaire et de l'aperçu PDF lisible
              </p>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="border-t border-slate-200 pt-8 mb-6">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h4 className="text-sm font-semibold text-amber-900 mb-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Clause de non-responsabilité
              </h4>
              <p className="text-xs text-amber-800 leading-relaxed mb-2">
                Ce valideur est fourni à titre informatif et d'aide au développement. Bien qu'il soit conçu pour valider la conformité aux normes AFNOR FE – XP Z12-012, UBL 2.1 et EN16931, 
                <strong> nous ne garantissons pas l'exactitude absolue ni l'exhaustivité des résultats de validation</strong>.
              </p>
              <p className="text-xs text-amber-800 leading-relaxed mb-2">
                L'utilisateur reste seul responsable de la conformité de ses documents et de leur utilisation. 
                Ce service est fourni "tel quel" sans garantie d'aucune sorte, expresse ou implicite.
              </p>
              <p className="text-xs text-amber-800 leading-relaxed">
                Nous déclinons toute responsabilité en cas d'erreurs, d'omissions ou de conséquences découlant de l'utilisation de cet outil. 
                Il est recommandé de vérifier les résultats et de consulter les textes officiels des normes pour toute utilisation en production.
              </p>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-slate-200 pt-6 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-500">
            <p>
              © {new Date().getFullYear()} Valideur Réforme Électronique France. Tous droits réservés.
            </p>
            <p className="flex items-center gap-2">
              <span>Conforme à la norme AFNOR XP Z12-012 du 30/06/2026 — norme 2026</span>
              <span className="mx-1">•</span>
              <span>Version 2.1</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ValidatorPage;